import { billingChargeKindLabel, isOfflinePaymentMethod } from './billing.js';
// TP-26: 適格請求書（インボイス制度）/ 領収書の帳票データ組立（純関数）。
//
// 料金は「税抜本体 ＋ 消費税（外税）」（契約 別紙1 / 第5項「消費税等は別途加算する」）。
//   本体価格を真実源とし、消費税 = floor(本体 × 税率)（税率ごとに合算して端数処理 1 回）、
//   税込 = 本体 ＋ 消費税。実際にカードから引き落とす額は税込（= amountYen）。
//   billing_charges は netYen（本体）/ taxYen（消費税）/ amountYen（税込）を持つ（TP-26 以降に
//   自前で課金する初期費用・初月日割り）。Webhook 経由の月額定期 / 旧データは amountYen（税込）
//   しか持たないため、その場合のみ税込から本体を逆算する（料金は 10% で割り切れる前提＝逆算は正確）。
//
// PDF ライブラリは導入せず、saas-admin 側で HTML 印刷ビュー（@media print）として描画する。
// Web/DOM 非依存の pure 関数のみ（@kawai/shared は functions(Node) からも参照されるため）。
/** 標準税率（消費税 10%）。軽減税率対象（8%）の SaaS 利用料は無い前提。 */
export const STANDARD_TAX_RATE = 0.1;
/**
 * カワイ株式会社（TracePit / セルフ武生21SS の運営）の適格請求書発行事業者情報。
 *
 * 実値は会社HP（https://kawai-ubo.jp/）と国税庁 適格請求書発行事業者公表サイト
 * （法人番号 5210001011565 / 登録番号 T5210001011565）で確認した確定値。
 * 社名・登録番号・所在地はいずれも公開情報のため shared 定数で保持する。
 */
export const INVOICE_ISSUER = {
    name: 'カワイ株式会社',
    registrationNumber: 'T5210001011565',
    postalCode: '915-0882',
    address: '福井県越前市上太田町29-15-1',
    tel: '0778-22-0255',
};
/**
 * カワイ株式会社の振込先口座（振込請求書の「お振込先」欄に印字される確定実値）。
 * 2026-07-04 にユーザー（カワイ株式会社 / 単独運用者）から直接提供を受けた一次情報
 * （会社情報は捏造せず必ず裏取りする規約 / TP-26 と同方針）。
 * 型を `InvoiceBankAccount | null` のまま維持するのは、null（未設定）時に発行画面が
 * 印刷をブロックする防御経路（InvoicePrintPage.bankAccountMissing）を型で残すため。
 */
export const INVOICE_BANK_ACCOUNT = {
    bankName: '福井銀行',
    bankCode: '0147',
    branchName: '武生支店',
    branchCode: '220',
    accountType: '普通',
    accountNumber: '0001187',
    accountHolder: 'カワイ株式会社',
};
/**
 * 税抜本体額から消費税・税込を求める（外税 / 本体起点）。
 *
 * 消費税 = floor(本体 × 税率)、税込 = 本体 ＋ 消費税。税率は整数パーセントに正規化してから
 * 乗算する（`本体 × 0.1` の浮動小数点誤差を避ける）。適格請求書の「税率ごとに 1 回の端数処理」を
 * 満たすため、複数行は税率ごとに本体を合算してから本関数を 1 回だけ適用する（summarizeTaxByRate）。
 */
export function computeTaxFromNet(netYen, taxRate = STANDARD_TAX_RATE) {
    const ratePercent = Math.round(taxRate * 100);
    const taxYen = Math.floor((netYen * ratePercent) / 100);
    return { taxRate, netYen, taxYen, grossYen: netYen + taxYen };
}
/**
 * 税込額から税抜本体・消費税を逆算する（内税 / 税込起点 / フォールバック用）。
 *
 * netYen = floor(税込 × 100 / (100 + 税率%))、消費税 = 税込 − netYen。端数処理は floor 1 回。
 * billing_charges が netYen を持たない旧データ / Webhook 経由の月額定期を請求書化するときだけ使う
 * （料金が 10% で割り切れる前提では本体起点と一致する）。
 */
export function deriveTaxBreakdown(grossYen, taxRate = STANDARD_TAX_RATE) {
    const ratePercent = Math.round(taxRate * 100);
    const netYen = Math.floor((grossYen * 100) / (100 + ratePercent));
    const taxYen = grossYen - netYen;
    return { taxRate, netYen, taxYen, grossYen };
}
/**
 * 記録額（実課金額）に対応する外税内訳を返す。記録額が算出時の期待額と一致すれば算出時の内訳
 * （本体起点の正確値）を、相違すれば記録額から逆算した内訳を返す。いずれの経路でも
 * netYen ＋ taxYen ＝ 記録額 の恒等式を維持し、内訳と総額が食い違う適格請求書を構造的に防ぐ。
 * billing_charges の全書込経路（初期費用 / 初月日割り / reconcile 補填）で共用する。
 */
export function reconcileChargeBreakdown(recordedAmountYen, expectedAmountYen, expected, taxRate = STANDARD_TAX_RATE) {
    if (recordedAmountYen === expectedAmountYen) {
        return { netYen: expected.netYen, taxYen: expected.taxYen };
    }
    const derived = deriveTaxBreakdown(recordedAmountYen, taxRate);
    return { netYen: derived.netYen, taxYen: derived.taxYen };
}
/**
 * 明細行（本体価格）を税率ごとに集計し、税率ごとに 1 回端数処理した内訳の配列を返す（税率昇順）。
 * 適格請求書の必須記載事項④⑤（税率ごとに区分した対価合計と消費税額）を満たす（外税 / 本体起点）。
 */
export function summarizeTaxByRate(lineItems) {
    const netByRate = new Map();
    for (const item of lineItems) {
        netByRate.set(item.taxRate, (netByRate.get(item.taxRate) ?? 0) + item.netYen);
    }
    return [...netByRate.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([rate, net]) => computeTaxFromNet(net, rate));
}
/**
 * 帳票番号を charge から決定論的に導出する（再発行で不変 / charge ごとに一意）。
 * 形式: `INV-YYYYMMDD-<chargeId>`（領収書は `RCP-`）。
 */
export function formatInvoiceNumber(charge, type) {
    const prefix = type === 'receipt' ? 'RCP' : 'INV';
    const datePart = toDateString(charge.createdAt).replace(/-/g, '');
    return `${prefix}-${datePart}-${charge.id}`;
}
/** Iso8601(JST) の先頭 10 文字を DateString として取り出す（文字列は JST 前提）。 */
function toDateString(iso) {
    return iso.slice(0, 10);
}
/**
 * billing_charges 1 件から適格請求書 / 領収書の帳票データを組み立てる（外税 / 本体起点）。
 *
 * 取引内容は charge.description（無ければ種別ラベル）。取引年月日は charge.createdAt。
 * 本体額は charge.netYen を真実源にし、無ければ amountYen（税込）から逆算する。
 * 消費税・税込は本体から算出するため、帳票は常に「本体 ＋ 消費税 ＝ 税込」の外税表示になる。
 */
export function buildInvoiceDocument(args) {
    const { charge, recipientName, type, issuedDate, issuer = INVOICE_ISSUER, taxRate: taxRateArg, bankAccount = INVOICE_BANK_ACCOUNT, } = args;
    const taxRate = charge.taxRate ?? taxRateArg ?? STANDARD_TAX_RATE;
    // 税内訳: netYen があれば本体を真実源にし、税込は実課金額（amountYen）を保持する（外税 / TP-26 以降）。
    // 無ければ amountYen（税込）から逆算する（旧データ / Webhook 経由の月額定期）。
    // 記録済みの内訳が netYen + taxYen = amountYen を満たさない doc（想定外の部分更新等）は、
    // 内訳と総額が食い違う帳票を出さないよう総額を真実源に逆算へフォールバックする。
    // いずれの経路でも netYen + taxYen = amountYen（税込合計＝実際にカードから引き落とした額）を厳守する。
    const storedTaxYen = charge.netYen != null ? (charge.taxYen ?? charge.amountYen - charge.netYen) : null;
    const breakdown = charge.netYen != null &&
        storedTaxYen != null &&
        charge.netYen + storedTaxYen === charge.amountYen
        ? {
            taxRate,
            netYen: charge.netYen,
            taxYen: storedTaxYen,
            grossYen: charge.amountYen,
        }
        : deriveTaxBreakdown(charge.amountYen, taxRate);
    const description = charge.description ?? billingChargeKindLabel(charge.kind);
    const lineItems = [
        { description, netYen: breakdown.netYen, taxRate },
    ];
    const taxSummary = [breakdown];
    const totalNetYen = breakdown.netYen;
    const totalTaxYen = breakdown.taxYen;
    const totalGrossYen = breakdown.grossYen;
    // TP-27: 振込請求書のみ「お振込先」欄を組み立てる。領収書（入金後）には振込先を載せない。
    const bankTransfer = type === 'invoice' && charge.paymentMethod === 'bank_transfer'
        ? { account: bankAccount, dueDate: charge.dueDate }
        : undefined;
    // TP-34: 口座振替の請求書は「お振込先」欄を出さず、口座振替で引き落とす旨のみ表示する。
    const directDebit = type === 'invoice' && charge.paymentMethod === 'direct_debit';
    // TP-27/TP-34: 手動請求（銀行振込・口座振替）の領収書は「入金 / 引落成立の事実」を証する
    // 書面のため、取引年月日は消し込み日（paidAt）を用いる。カード決済 / 請求書 /
    // paidAt 未記録は従来どおり createdAt。
    const transactionDate = type === 'receipt' &&
        isOfflinePaymentMethod(charge.paymentMethod) &&
        charge.paidAt != null
        ? toDateString(charge.paidAt)
        : toDateString(charge.createdAt);
    return {
        type,
        documentNumber: formatInvoiceNumber(charge, type),
        issuedDate,
        transactionDate,
        issuer,
        recipientName,
        lineItems,
        taxSummary,
        totalNetYen,
        totalTaxYen,
        totalGrossYen,
        ...(bankTransfer ? { bankTransfer } : {}),
        ...(directDebit ? { directDebit: true } : {}),
    };
}
/** 帳票種別の日本語表題（請求書 / 領収書）。 */
export function invoiceDocumentTypeLabel(type) {
    return type === 'receipt' ? '領収書' : '請求書';
}
/** 税率を百分率表記（例 0.1 → '10%'）に整形。 */
export function formatTaxRate(taxRate) {
    return `${Math.round(taxRate * 100)}%`;
}
//# sourceMappingURL=invoice.js.map